import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { glacierPolicyApiReopenUnlockedAccountMutation } from '@/lib/heyapi/@tanstack/react-query.gen';

import { formatErrorMessage } from '../../utils';
import type { Message } from '../StandardMessage';
import { ErrorDialog } from './ErrorDialog';

// Component for the confirmation dialog to reopen an account
const ReopenUnlockedAccountDialog = ({
  isOpen,
  onOpenChange,
  setMessage,
  policyQK,
  policyId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  policyQK: QueryKey;
  policyId: string;
}) => {
  const queryClient = useQueryClient();

  const reopenUnlockedAccount = useMutation({
    ...glacierPolicyApiReopenUnlockedAccountMutation(),
    onSuccess() {
      setMessage({
        content:
          'Account reopened successfully. Quote type and policy number have been reset.',
        level: 'SUCCESS',
      });
      queryClient.refetchQueries({ queryKey: policyQK });
      // Delay 1 second and reload the page
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape needs ApiError type (future ticket)
    onError(error: any) {
      console.error('Reopen account error:', error);
      setMessage({
        content: (
          <ErrorDialog
            title="Error reopening account"
            onClose={() => setMessage(undefined)}
          >
            {formatErrorMessage(error)}
          </ErrorDialog>
        ),
        level: 'ERROR',
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Reopen Unbilled Account
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to re-open this unbilled account? This will
            remove the policy number and reset the quote type.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              reopenUnlockedAccount.mutate({
                path: { account_id: policyId },
              });
              onOpenChange(false);
            }}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            Reopen Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReopenUnlockedAccountDialog;
